import React, { useState, useEffect } from 'react';
import './User.scss';
import api from '../../api';
import DynamicUserTable from '../../components/tablewithpaginationandlimit/user-table';
import Filters from '../../components/filter/filters';
import { Container, Row, Col } from 'react-bootstrap';
import { FaEdit, FaCircle } from 'react-icons/fa';
import { IoPersonAddSharp } from 'react-icons/io5';
import SearchBar from '../../components/searchBar/searchBar';
import { useNavigate } from 'react-router-dom';
import TableSkeletonLoader from '../../components/loader/table-skeleton-loader/table-skeleton-loader';
import Limitdropup from '../../components/limit-drop-up/limit-drop-up';
import Pagination from '../../components/pagination/pagination';
import { useSelector } from 'react-redux';

const User = () => {
    const [user, setUser] = useState([]);
    const [filter, setFilter] = useState({});
    const [sort, setSort] = useState([]);
    const [limit, setLimit] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [pages, setPages] = useState(0);
    const [filterData, setFilterData] = useState([]);
    const [search, setSearch] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [emptyFilter, setEmptyFilter] = useState(false);
    const [emptySearchbar, setEmptySearchbar] = useState(false);
    const authState = useSelector((state) => state.authUser);
    // const role_id = authState?.profile?.role?.role_id;
    const userRoles = authState?.userRoles || [];

    const hasRole = (roleId) => userRoles.some(role => role.role_id === roleId);
    const navigate = useNavigate();

    // Update request to send only role_ids
    const request = {
        resource: 'api/users',
        limit,
        currentPage,
        search,
        sort,
        filter: {
            ...filter,
            role: filter?.role?.map(r => r.role_id) || [] // <-- send only role_ids
        }
    };

    const handlingLimit = (limit) => { setLimit(limit); setCurrentPage(1); };
    const handlePageChange = (newPage) => { setCurrentPage(newPage); };
    const handleSort = (value) => { setSort(value); setCurrentPage(1); };
    const handleSearch = (value) => {
        setEmptyFilter(!emptyFilter);
        setFilter({ dataSource: [], checktypes: [] });
        setSearch(value);
        setCurrentPage(1);
    };
    const handleFilter = (value) => {
        setEmptySearchbar(!emptySearchbar);
        setSearch('');
        setFilter(value);
        setCurrentPage(1);
    };
    const handleEdit = (row) => {
        navigate(`/edituser/${row.original.user_id}`, {
            state: { redirectTo: '/users' }
        });
    };

    const columns = [
        {
            Header: 'User',
            Cell: ({ row }) => (
                <div className="d-flex flex-row gap-1 color">
                    <div style={{
                        width: '38px', backgroundColor: '#1F67C6', height: '37px',
                        borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'flex-end', flexWrap: 'wrap'
                    }}>
                        <h6 className="text-light text-uppercase d-flex flex-row pt-1">
                            <div>{row.original.first_name.charAt(0)}</div>
                            <div>{row.original.last_name.charAt(0)}</div>
                        </h6>
                    </div>
                    <div className="pt-2">
                        <div className="fw-bolder" style={{ fontSize: '15px' }}>{row.original.display_name}</div>
                        <div className="text-secondary" style={{ fontSize: '15px' }}>{row.original.email}</div>
                    </div>
                </div>
            ),
        },
        { Header: 'First Name', accessor: 'first_name' },
        { Header: 'Last Name', accessor: 'last_name' },
        {
            Header: 'Role',
            Cell: ({ row }) => {
                const roles = Array.isArray(row.original.roles) ? row.original.roles : [];
                if (roles.length === 0) return '—';
                const formattedRoles = roles.map(r =>
                    r.role_name.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
                );
                return formattedRoles.length > 2
                    ? `${formattedRoles.slice(0, 2).join(', ')} ...`
                    : formattedRoles.join(', ');
            },
        },
        {
            Header: 'Status',
            Cell: ({ row }) => (
                <div className="d-flex flexx-row gap-1">
                    <FaCircle className={`pt-2 ${row.original.is_active ? 'text-success' : 'text-danger'}`} />
                    <div>{row.original.is_active ? 'Active' : 'Inactive'}</div>
                </div>
            ),
        },
        {
            Header: 'Action',
            Cell: ({ row }) => (
                <FaEdit className="text-dark mx-2 font-size" onClick={() => handleEdit(row)} />
            ),
        },
    ];

    const fetchUserTable = async () => {
        try {
            setIsLoading(true);
            const userData = await api.crud.getList(request);
            setPages(userData.data.pages);
            setUser(userData.data.data.rows);
            setIsLoading(false);
        } catch (error) {
            console.log(error);
            setIsLoading(false);
        }
    };

    const fetchUserRole = async () => {
        try {
            const requestUserList = { resource: 'api/roles' };
            const userList = await api.rolelist.getRole(requestUserList);

            const roleMap = {};
            userList?.data?.data?.forEach(role => {
                const formattedName = role.role_name
                    .split('_')
                    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                    .join(' ');

                if (role.parent_id) {
                    if (!roleMap[role.parent_id]) roleMap[role.parent_id] = [];
                    roleMap[role.parent_id].push({
                        role_id: role.role_id,
                        role_name: formattedName,
                        isHeading: false,
                        indent: true
                    });
                }
            });

            const arr = userList?.data?.data
                .filter(role => !role.parent_id)
                .map(role => {
                    const roleNameFormatted = role.role_name.split('_')
                        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                        .join(' ');

                    const isNonSelectableHeading = ['Admin', 'Viewer'].includes(roleNameFormatted);

                    return {
                        role_id: role.role_id,
                        role_name: roleNameFormatted,
                        isHeading: isNonSelectableHeading,
                        children: roleMap[role.role_id] || [],
                    };
                });

            const flatRoles = [];
            arr.forEach(parent => {
                flatRoles.push({ role_id: parent.role_id, role_name: parent.role_name, isHeading: parent.isHeading });
                parent.children.forEach(child => flatRoles.push(child));
            });

            setFilterData(flatRoles);
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => { fetchUserRole(); }, []);
    useEffect(() => { fetchUserTable(); }, [limit, currentPage, sort, search, filter]);
    useEffect(() => { if (!hasRole(6)) navigate('/home'); }, []);

    return (
        <Container>
            <div className="d-flex justify-content-between">
                <h5 className="pagetitle pt-4 fw-bold">Manage Users</h5>
                <div className="d-flex gap-2 mt-3 mb-3">
                    <button
                        type="button"
                        className="btn btn text-light custom_button d-flex flex-row"
                        style={{ background: '#3B6FD0', border: 'solid 1px #3B6FD0' }}
                        onClick={() => navigate('/addUser', { state: { redirectTo: '/users' } })}
                    >
                        <IoPersonAddSharp size={'15px'} style={{ marginRight: '7px', marginBottom: '4px' }} />
                        <span>Add User</span>
                    </button>
                </div>
            </div>

            <Row>
                <Col md={4} className="my-1">
                    <SearchBar handleSearch={handleSearch} emptySearchbar={emptySearchbar} placeholder={'Emp Name'}/>
                </Col>
                <Col md={8}>
                    <Filters
                        filterData={filterData}
                        handleFilter={handleFilter}
                        emptyFilter={emptyFilter}
                        statusData={['Active', 'Inactive']}
                        redirectTo="/users"
                    />
                </Col>
            </Row>

            {isLoading ? (
                <TableSkeletonLoader intialrows={8} initalcolumns={5} />
            ) : (
                <>
                    <DynamicUserTable
                        row={user}
                        columns={columns}
                        isPagination={false}
                        isLimit={false}
                        pages={pages}
                        limit={limit}
                    />
                    <div className="d-flex justify-content-end mx-auto w-100">
                        <Row>
                            <div className="d-flex mx-3">
                                <div className="py-2 font-weight-semibold">Records per page</div>
                                <Limitdropup limit={limit} handlingLimit={handlingLimit} />
                            </div>
                        </Row>
                        <div className="py-1">
                            <Pagination currentPage={currentPage} handlePageChange={handlePageChange} pages={pages} />
                        </div>
                    </div>
                </>
            )}
        </Container>
    );
};

export default User;
